/* eslint-disable eqeqeq */
import { ResponsiveLine } from "@nivo/line";
import { extent } from 'd3-array'; // Import extent from d3-array
import _ from "lodash";
import moment from "moment";
import * as React from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import { CHART_POINT_TYPE } from "../../../../types/chart-dialog.type";
import { FILTER_TYPES } from "../../../../types/common.type";
import { ADMISSION_CARDS_TYPE } from "../../../../types/admission.type";
import { getPositivePlusSign, itemPercentageChart } from "../../../../utilis/common";
import styles from "../ChartDialogCommon.module.scss";

export default function LineChartDialog({
	chartData,
	chartPointType,
	selectedFacility,
	selectedFilter,
	isSeparateLine,
	censusAverage,
	isOverallTotal,
	isPlusSign,
	cardType = null, // Default to null to ensure backward compatibility
	isShowCensus = false,
	dailyCensusTotal = []
}) {
	const [data, setData] = React.useState([]);
	const [singleLineChart, setSingleLineChart] = React.useState([]);
	const { defaultFacilities } = useSelector((state) => state.activeFacilities);
	const isMobileWidth = useMediaQuery('(max-width: 768px)');
	const isMobileLandscape = useMediaQuery('(max-height: 500px) and (max-width: 960px)');
	const isMobile = isMobileWidth || isMobileLandscape;

	const updateChartDataByFacility = (chartData, facility, defaultFacilities, ele) => {
		const indexFind = chartData.findIndex((facilityBy) => facilityBy.keyId === facility);
		const selectedFacility = _.find(defaultFacilities, { _id: facility });
		const facilityName = selectedFacility?.name || "Unknown Facility";

		if (indexFind === -1) {
			// Add new facility entry
			chartData.push({
				id: facilityName,
				keyId: facility,
				label: facilityName,
				total: 0,
				data: [{
					x: ele?.x,
					y: 0,
					percentage: 0,
					facilityIds: ele?.facilityIds || [],
					label: facilityName,
				}]
			});
		} else {
			// Update existing facility entry
			const existingArray = chartData[indexFind];
			const newTotal = 0;  // You can adjust this logic if needed
			const updatedTotal = existingArray.total + newTotal;

			const updateData = {
				x: ele?.x,
				y: newTotal,
				percentage: ele?.percentage || 0,
				facilityIds: ele?.facilityIds || [],
			};

			chartData[indexFind] = {
				...existingArray,
				total: updatedTotal,
				percentage: 0,
				id: `${existingArray.label} (${updatedTotal})`,
				data: [...existingArray.data, updateData],
			};
		}
	};

	React.useEffect(() => {
		async function fetchMyAPI() {
			let chartDataUpdated = [];
			let chartDataByFacility = [];
			// Only apply average calculation logic when cardType is explicitly AVERAGE_STAY_DAYS_DATA
			// For all other cases (null, undefined, or other card types), use original behavior
			const isAverageStayDays = cardType === ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA;

			// Helper function to calculate average transferDay or count
			// Only affects behavior when cardType is AVERAGE_STAY_DAYS_DATA
			// For all other card types, returns patientArray.length (original behavior)
			const calculateValue = (patientArray) => {
				if (isAverageStayDays) {
					// Only calculate average for average stay days card type
					const validTransferDays = (patientArray || [])
						.map(item => item.transferDay)
						.filter(day => day != null && !isNaN(day) && day > 0);

					if (validTransferDays.length > 0) {
						const sum = validTransferDays.reduce((acc, day) => acc + day, 0);
						return sum / validTransferDays.length;
					}
					return 0;
				}
				// Original behavior: return patient count
				return patientArray?.length || 0;
			};

			const formatSeriesTotal = (value, projected) => {
				// Keep projected values clean for chart labels/legend
				if (projected && typeof value === "number" && !Number.isNaN(value)) {
					return Math.round(value);
				}
				// Preserve average-stay formatting behavior for non-projected values
				if (isAverageStayDays && typeof value === "number" && !Number.isNaN(value)) {
					return value.toFixed(1);
				}
				return value;
			};

			chartData.forEach(async (ele, index) => {
				const facilityUpdated = ele.facilityIds;
				const facilityWiseDataUpdated = ele.facilityWiseData;

				if (facilityWiseDataUpdated && !_.isEmpty(facilityWiseDataUpdated)) {
					const updatedKeys = Object.keys(facilityWiseDataUpdated);
					const missingIDs = selectedFacility.filter(id => !updatedKeys.includes(id));
					for (const [key, value] of Object.entries(facilityWiseDataUpdated)) {

						let indexFind = chartDataByFacility.findIndex((facilityBy) => facilityBy.keyId === key);

						let facilityName = value && value.length > 0 ? value[0]?.facility?.name : null;

						if (indexFind === -1) {
							if (selectedFacility.includes(key)) {
								// Calculate original value for this facility from actual data
								let originalTotal = calculateValue(value);

								if (isOverallTotal && !isAverageStayDays) {
									const allOverAll = _.groupBy(value, "isOutgoing");
									let totalIN = 0;
									let totalOut = 0;
									for (const [key, value] of Object.entries(allOverAll)) {
										const total = value.length;
										if (key !== undefined) {
											if (key == "true") {
												totalOut = total;
											} else {
												totalIN = total;
											}
										}
									}
									originalTotal = totalIN - totalOut;
								}

								// If projected, calculate projected value for this facility using the same ratio
								let total = originalTotal;
								let facilityOriginalY = originalTotal;
								if (ele?.isProjected && ele?.originalY != null && ele?.originalY !== 0 && ele?.y != null) {
									// Calculate projection ratio from overall period
									const projectionRatio = ele.y / ele.originalY;
									total = originalTotal * projectionRatio;
									// Store original value for tooltip
									facilityOriginalY = originalTotal;
								}

								const percentage = itemPercentageChart(total, censusAverage, "percentage") || 0;

								chartDataByFacility.push({
									id: `${facilityName} (${formatSeriesTotal(total, ele?.isProjected)})`,
									keyId: key,
									label: facilityName,
									total,
									data: [
										{
											x: ele?.x,
											y: total,
											percentage,
											facilityIds: ele.facilityIds,
											label: facilityName,
											isProjected: ele?.isProjected || false,
											originalY: ele?.isProjected ? facilityOriginalY : undefined,
										},
									],
								});
							}
						} else {

							let existingArray = chartDataByFacility[indexFind];

							// Calculate original value for this facility from actual data
							let originalNewTotal = calculateValue(value);

							if (isOverallTotal && !isAverageStayDays) {
								const allOverAll = _.groupBy(value, "isOutgoing");
								let totalIN = 0;
								let totalOut = 0;
								for (const [key, value] of Object.entries(allOverAll)) {
									const total = value.length;
									if (key !== undefined) {
										if (key == "true") {
											totalOut = total;
										} else {
											totalIN = total;
										}
									}
								}
								originalNewTotal = totalIN - totalOut;
							}

							// If projected, calculate projected value for this facility using the same ratio
							let newTotal = originalNewTotal;
							let facilityOriginalY = originalNewTotal;
							if (ele?.isProjected && ele?.originalY != null && ele?.originalY !== 0 && ele?.y != null) {
								// Calculate projection ratio from overall period
								const projectionRatio = ele.y / ele.originalY;
								newTotal = originalNewTotal * projectionRatio;
								// Store original value for tooltip
								facilityOriginalY = originalNewTotal;
							}

							let updateData = {
								x: ele?.x,
								y: newTotal,
								percentage: ele.percentage,
								facilityIds: ele.facilityIds,
								isProjected: ele?.isProjected || false,
								originalY: ele?.isProjected ? facilityOriginalY : undefined,
							};

							// For average stay days, calculate running average; otherwise sum (original behavior)
							let updatedTotal;
							if (isAverageStayDays) {
								// Only for average stay days: calculate average of averages
								const existingCount = existingArray.data.length;
								const newCount = 1;
								updatedTotal = ((existingArray.total * existingCount) + (newTotal * newCount)) / (existingCount + newCount);
							} else {
								// Original behavior: sum the totals
								updatedTotal = existingArray?.total + newTotal;
							}

							const percentage = itemPercentageChart(updatedTotal, censusAverage, "percentage") || 0;

							chartDataByFacility[indexFind] = {
								...existingArray,
								total: updatedTotal,
								percentage: percentage,
								id: `${existingArray.label} (${formatSeriesTotal(updatedTotal, ele?.isProjected)})`,
								data: [...existingArray?.data, updateData],
							};
						}
					}
					if (missingIDs.length > 0) {
						missingIDs.forEach((facility) => {
							updateChartDataByFacility(chartDataByFacility, facility, defaultFacilities, ele);
						});
					}
				} else {
					selectedFacility.forEach((facility) => {
						updateChartDataByFacility(chartDataByFacility, facility, defaultFacilities, ele);
					});
				}

				// If this is a projected data point, use the pre-calculated projected y value directly
				// The projection was already calculated in chartGroupBy, so we should use it as-is
				let count = 0;
				if (ele?.isProjected && ele?.y != null && typeof ele.y === 'number') {
					// Use the projected y value directly from chartGroupBy
					count = ele.y;
				} else if (isAverageStayDays) {
					// Only for average stay days: calculate average transferDay
					// For average stay days, use the y value from chartData (already calculated average)
					// or calculate from facility-wise data if available
					if (facilityWiseDataUpdated && !_.isEmpty(facilityWiseDataUpdated)) {
						const allPatients = [];
						facilityUpdated.forEach((i) => {
							if (selectedFacility.includes(i) && facilityWiseDataUpdated[i]) {
								allPatients.push(...facilityWiseDataUpdated[i]);
							}
						});
						count = calculateValue(allPatients);
					} else {
						// Use the pre-calculated average from chartsData function
						count = ele?.y || 0;
					}
				} else if (isOverallTotal) {
					// Original behavior: calculate overall total (incoming - outgoing)
					facilityUpdated.forEach((i) => {
						if (selectedFacility.includes(i)) {
							const patientLists = facilityWiseDataUpdated[i];
							const allOverAll = _.groupBy(patientLists, "isOutgoing");
							let totalIN = 0;
							let totalOut = 0;
							for (const [key, value] of Object.entries(allOverAll)) {
								const total = value.length;
								if (key !== undefined) {
									if (key == "true") {
										totalOut = total;
									} else {
										totalIN = total;
									}
								}
							}
							count = totalIN - totalOut;
						}
					});
				} else {
					// If facilityWiseData is null/empty AND facilityIds is empty, use pre-calculated y value
					// This ONLY applies when averages are pre-calculated (e.g., for isCustomDateAnalysis average items)
					// We only clear both facilityWiseData and facilityIds in OverallCardDetailsDialog when:
					// - isCustomDateAnalysis is true AND an average item (isSpacialItem === true) is selected
					// So if both are empty here, it's safe to use the pre-calculated y value
					if ((!facilityWiseDataUpdated || _.isEmpty(facilityWiseDataUpdated)) &&
						(!facilityUpdated || facilityUpdated.length === 0) &&
						ele?.y != null && typeof ele.y === 'number') {
						// Use the pre-calculated y value directly (for average items in isCustomDateAnalysis)
						count = ele.y;
					} else {
						// Original behavior: count facilities
						facilityUpdated.forEach((i) => (selectedFacility.includes(i) ? count++ : count));
					}
				}
				// If showing census mode, replace y with census value from dailyCensusTotal
				let finalY = count;
				if (isShowCensus && dailyCensusTotal && dailyCensusTotal.length > 0) {
					// Match date from chart data with dailyCensusTotal
					const chartDate = ele?.x ? moment(ele.x).format("YYYY-MM-DD") : null;
					if (chartDate) {
						const censusData = dailyCensusTotal.find(item => item.date === chartDate);
						if (censusData) {
							finalY = censusData.total;
						}
					}
				}

				const pointData = {
					x: ele?.z,
					y: finalY,
					percentage: ele.percentage,
					facilityIds: ele.facilityIds,
					isProjected: ele?.isProjected || false,
					originalY: ele?.originalY, // Use originalY directly from charts-common.js
				};

				chartDataUpdated.push(pointData);
			});

			setSingleLineChart(chartDataUpdated);

			setData(chartDataByFacility);
		}

		fetchMyAPI();
	}, [chartData, selectedFacility, censusAverage, isOverallTotal, defaultFacilities, cardType, isShowCensus, dailyCensusTotal]);

	const getRequiredDateFormat = (timeStamp, format = "MM-DD-YYYY") => {
		return moment.utc(timeStamp).format(format);
	};

	const calculateYDomain = (data) => {
		if (!data || data.length === 0) {
			return [0, 10];
		}

		const allYValues = data.flatMap((serie) => serie.data.map((point) => point.y));
		const [minY, maxY] = extent(allYValues);
		const lowest = Math.min(0, minY);
		const range = maxY - lowest;
		const padding = range * 0.12 || 1;

		return [lowest, maxY + padding];
	};

	const calculateSingleYDomain = (singleLineData) => {
		if (!singleLineData || singleLineData.length === 0) {
			return [0, 10];
		}

		const allYValues = singleLineData.map((point) => point.y);
		const [minY, maxY] = extent(allYValues);
		const lowest = Math.min(0, minY);
		const range = maxY - lowest;
		const padding = range * 0.12 || 1;

		return [lowest, maxY + padding];
	};

	const getTickValues = (chartDataPoints) => {
		if (!chartDataPoints || chartDataPoints.length === 0) return undefined;
		const maxTicks = isMobile ? 6 : 12;
		if (chartDataPoints.length <= maxTicks) {
			return chartDataPoints.map(p => p.x);
		}
		const interval = Math.ceil(chartDataPoints.length / maxTicks);
		return chartDataPoints
			.filter((_, idx) => idx % interval === 0)
			.map(p => p.x);
	};

	// Custom tooltip component for projected points
	const CustomTooltip = ({ point }) => {
		const datum = point.data;
		const isProjected = datum?.isProjected || false;
		const originalY = datum?.originalY;
		const projectedY = datum?.y;

		// Safely format x value for display (handle Date objects from time scale)
		const rawX = datum?.x;
		const xLabel =
			rawX instanceof Date
				? moment(rawX).format("MM/DD/YYYY")
				: rawX != null
					? String(rawX)
					: "";

		// Format value based on chart point type
		const formatValue = (value) => {
			if (chartPointType === CHART_POINT_TYPE.PERCENTAGE) {
				return datum?.percentage || 0;
			}
			if (isShowCensus) {
				return value;
			}
			if (isProjected && typeof value === "number" && !Number.isNaN(value)) {
				return Math.round(value);
			}
			return isPlusSign ? getPositivePlusSign(value) : value;
		};

		if (isProjected && originalY != null && originalY !== undefined) {
			return (
				<div style={{
					background: 'white',
					padding: '8px 12px',
					border: '1px solid #ccc',
					borderRadius: '4px',
					boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
					fontSize: isMobile ? '12px' : '14px',
				}}>
					<div style={{ color: '#666', marginBottom: '2px' }}>
						Original: <strong>{formatValue(originalY)}</strong>
					</div>
					<div style={{ color: '#FF8C00', fontWeight: 'bold' }}>
						Projected: <strong>{formatValue(projectedY)}</strong>
					</div>
					<div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
						{xLabel}
					</div>
				</div>
			);
		}

		// Default tooltip for non-projected points
		return (
			<div style={{
				background: 'white',
				padding: '8px 12px',
				border: '1px solid #ccc',
				borderRadius: '4px',
				boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
				fontSize: isMobile ? '12px' : '14px',
			}}>
				<div>
					{formatValue(projectedY)}
				</div>
				<div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
					{xLabel}
				</div>
			</div>
		);
	};

	return (
		<div className={styles.chartContainer} id="svg-container">
			{isSeparateLine ? (
				<ResponsiveLine
					data={data}
					xScale={{
						type: "time",
						format: "%m-%d-%Y",
					}}
					xFormat="time:%m-%d-%Y"
					axisBottom={{
						format: "%m-%d-%Y",
						orient: "bottom",
						...(selectedFilter === FILTER_TYPES.SEVEN_DAYS && {
							tickValues: isMobile ? "every 14 days" : "every 7 days",
						}),
						...(selectedFilter === FILTER_TYPES.MONTHLY && {
							format: (values) => `${getRequiredDateFormat(values, "MMM")}`,
							tickValues: isMobile ? "every 2 months" : "every month",
						}),
						...(selectedFilter === FILTER_TYPES.WEEKLY && {
							format: (values) =>
								isMobile
									? `${moment(values).startOf("week").format("MM/DD")}`
									: `(${moment(values).startOf("week").format("MM-DD")})-(${moment(values).endOf("week").format("MM-DD")})`,
							tickValues: isMobile ? "every 2 weeks" : "every week",
						}),
						...(selectedFilter === FILTER_TYPES.YEARLY && {
							format: (values) => `${getRequiredDateFormat(values, "YYYY")}`,
							tickValues: "every years",
						}),
						tickPadding: isMobile ? 8 : 5,
						tickRotation: isMobile ? -45 : -20,
						tickSize: isMobile ? 8 : 5,
					}}
					yScale={{
						type: "linear",
						min: 0,
						max: "auto",
						// stacked: true,
						reverse: false,
						tickPadding: isMobile ? 8 : 5,
						domain: calculateYDomain(data)
					}}
					legends={[
						{
							anchor: "bottom-left",
							direction: "row",
							translateX: 0,
							translateY: isMobile ? 50 : 70,
							itemsSpacing: isMobile ? 10 : 0,
							itemDirection: "left-to-right",
							itemWidth: isMobile ? 95 : 130,
							itemHeight: isMobile ? 16 : 20,
							itemOpacity: 0.75,
							symbolSize: isMobile ? 10 : 12,
							symbolShape: "circle",
						},
					]}
					margin={{
						top: isMobile ? 20 : 30,
						right: isMobile ? 15 : 20,
						bottom: isMobile ? 100 : 140,
						left: isMobile ? 50 : 70
					}}
					pointLabel={({ data }) => {
						if (chartPointType === CHART_POINT_TYPE.PERCENTAGE) {
							return data?.percentage;
						} else {
							// When showing census, display the actual census value without plus/minus signs
							if (isShowCensus) {
								return data?.y;
							}
							if (data?.isProjected && typeof data?.y === "number" && !Number.isNaN(data?.y)) {
								return Math.round(data?.y);
							}
							return isPlusSign ? getPositivePlusSign(data?.y) : data?.y;
						}
					}}
					enableGridX={false}
					animate={false}
					colors={{ scheme: "category10" }}
					axisTop={null}
					axisRight={null}
					pointColor={{ theme: "background" }}
					pointBorderWidth={isMobile ? 3 : 2}
					pointBorderColor={{ from: "serieColor" }}
					pointSymbol={({ datum, size, color, borderWidth, borderColor }) => {
						if (datum?.isProjected) {
							return (
								<rect
									x={-size / 2}
									y={-size / 2}
									width={size}
									height={size}
									fill="#FF8C00"
									stroke="#CC5500"
									strokeWidth={2}
									transform="rotate(45)"
								/>
							);
						}
						return (
							<circle
								r={size / 2}
								fill={color}
								stroke={borderColor}
								strokeWidth={borderWidth}
							/>
						);
					}}
					useMesh={data?.some(serie => serie.data?.length > 0)}
					enablePointLabel={true}
					pointSize={isMobile ? 10 : 8}
					tooltip={CustomTooltip}
				/>
			) : (
				<>
					<ResponsiveLine
						data={[
							{
								id: "graph",
								color: "hsl(19, 70%, 50%)",
								data: singleLineChart,
							},
						]}
						margin={{
							top: isMobile ? 20 : 30,
							right: isMobile ? 15 : 20,
							bottom: isMobile ? 90 : 120,
							left: isMobile ? 50 : 70
						}}
						xScale={{ type: "point" }}
						axisBottom={{
							orient: "bottom",
							tickSize: isMobile ? 3 : 1,
							tickPadding: isMobile ? 8 : 5,
							tickRotation: isMobile ? -45 : -20,
							tickValues: getTickValues(singleLineChart),
							format: (value) => {
								if (isMobile && typeof value === "string") {
									// Shorten weekly date range formats like "(03-01)-(03-07)" to "03/01"
									const match = value.match(/^\((\d{2})-(\d{2})\)-\((\d{2})-(\d{2})\)$/);
									if (match) {
										return `${match[1]}/${match[2]}`;
									}
									// Shorten daily/7-day date formats "MM-DD-YYYY" to "MM/DD"
									const dateMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
									if (dateMatch) {
										return `${dateMatch[1]}/${dateMatch[2]}`;
									}
								}
								return value;
							}
						}}
						yScale={{
							type: "linear",
							min: "auto",
							max: "auto",
							// stacked: true,
							reverse: false,
							tickPadding: isMobile ? 8 : 5,
							domain: calculateSingleYDomain(singleLineChart)
						}}
						pointLabel={({ data }) => {
							if (chartPointType === CHART_POINT_TYPE.PERCENTAGE) {
								return data?.percentage;
							} else {
								// When showing census, display the actual census value without plus/minus signs
								if (isShowCensus) {
									return data?.y;
								}
								if (data?.isProjected && typeof data?.y === "number" && !Number.isNaN(data?.y)) {
									return Math.round(data?.y);
								}
								return isPlusSign ? getPositivePlusSign(data?.y) : data?.y;
							}
						}}
						enableGridX={false}
						animate={false}
						axisTop={null}
						axisRight={null}
						pointColor={{ theme: "background" }}
						pointBorderWidth={isMobile ? 3 : 2}
						pointBorderColor={{ from: "serieColor" }}
						pointSymbol={({ datum, size, color, borderWidth, borderColor }) => {
							if (datum?.isProjected) {
								return (
									<rect
										x={-size / 2}
										y={-size / 2}
										width={size}
										height={size}
										fill="#FF8C00"
										stroke="#CC5500"
										strokeWidth={2}
										transform="rotate(45)"
									/>
								);
							}
							return (
								<circle
									r={size / 2}
									fill={color}
									stroke={borderColor}
									strokeWidth={borderWidth}
								/>
							);
						}}
						useMesh={singleLineChart?.length > 0}
						enablePointLabel={true}
						pointSize={isMobile ? 10 : 8}
						tooltip={CustomTooltip}
					/>
				</>
			)}
		</div>
	);
}
